class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.openDelay = Number(this.dataset.openDelay || 120);
    this.closeDelay = Number(this.dataset.closeDelay || 180);
    this.enableHover = this.dataset.enableHover !== 'false';
    this.openTimer = null;
    this.closeTimer = null;
    this.closeAnimTimer = null;
    this.activeId = null;
    this.leaving = new Map();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-mega-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-mega-panel]'));

    this.triggers.forEach((trigger) => {
      const id = trigger.getAttribute('aria-controls');
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle(id, trigger);
      });

      if (this.enableHover && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        trigger.addEventListener('mouseenter', () => this.scheduleOpen(id, trigger));
        trigger.addEventListener('focus', () => this.scheduleOpen(id, trigger));
      }
    });

    const desktop = this.querySelector('[data-mega-desktop]');
    if (desktop && this.enableHover) {
      desktop.addEventListener('mouseleave', () => this.scheduleClose());
      desktop.addEventListener('mouseenter', () => this.clearCloseTimer());
    }

    document.addEventListener('click', this.onDocumentClick);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    this.clearTimers();
    this.clearCloseAnimation();
    this.flushLeaving();
  }

  scheduleOpen(id, trigger) {
    this.clearTimers();
    this.openTimer = window.setTimeout(() => this.open(id, trigger), this.openDelay);
  }

  scheduleClose() {
    this.clearOpenTimer();
    this.closeTimer = window.setTimeout(() => this.close(), this.closeDelay);
  }

  clearCloseTimer() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  clearOpenTimer() {
    if (this.openTimer) {
      window.clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }

  clearCloseAnimation() {
    if (this.closeAnimTimer) {
      window.clearTimeout(this.closeAnimTimer);
      this.closeAnimTimer = null;
    }
    if (this._closeAnimPanel && this._closeAnimHandler) {
      this._closeAnimPanel.removeEventListener('animationend', this._closeAnimHandler);
    }
    this._closeAnimPanel = null;
    this._closeAnimHandler = null;
  }

  clearTimers() {
    this.clearOpenTimer();
    this.clearCloseTimer();
  }

  getAnimationDuration() {
    const raw = getComputedStyle(this).getPropertyValue('--mega-menu-animation-duration').trim();
    if (!raw) return 240;
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return 240;
    if (raw.endsWith('ms')) return value;
    if (raw.endsWith('s')) return value * 1000;
    return value;
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  animationsEnabled() {
    return (this.dataset.revealAnimation || 'fade') !== 'none' && !this.prefersReducedMotion();
  }

  flushLeaving() {
    Array.from(this.leaving.values()).forEach((cleanup) => cleanup());
  }

  startLeaving(panel) {
    panel.classList.remove('is-active');
    panel.classList.add('is-leaving');
    panel.hidden = false;

    const cleanup = () => {
      panel.removeEventListener('animationend', onAnimationEnd);
      window.clearTimeout(timer);
      this.leaving.delete(panel);
      panel.classList.remove('is-leaving');
      if (!panel.classList.contains('is-active')) panel.hidden = true;
    };

    const onAnimationEnd = (event) => {
      if (event.target !== panel) return;
      cleanup();
    };

    panel.addEventListener('animationend', onAnimationEnd);
    const timer = window.setTimeout(cleanup, this.getAnimationDuration() + 50);
    this.leaving.set(panel, cleanup);
  }

  toggle(id, trigger) {
    if (this.activeId === id && this.classList.contains('is-open') && !this.classList.contains('is-closing')) {
      this.close();
      return;
    }
    this.open(id, trigger);
  }

  open(id, trigger) {
    this.clearTimers();
    this.clearCloseAnimation();
    this.flushLeaving();

    const wasOpen = this.classList.contains('is-open') && !this.classList.contains('is-closing');
    const previous = this.panels.find((panel) => panel.classList.contains('is-active') && panel.id !== id);

    this.classList.remove('is-closing');
    this.activeId = id;
    this.classList.add('is-open');

    this.triggers.forEach((btn) => {
      const isActive = btn === trigger;
      btn.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });

    const animateSwitch = wasOpen && previous && this.animationsEnabled();

    this.panels.forEach((panel) => {
      if (panel.id === id) {
        panel.classList.remove('is-leaving');
        panel.classList.add('is-active');
        panel.hidden = false;
        return;
      }
      if (panel === previous && animateSwitch) return;
      panel.classList.remove('is-active');
      panel.hidden = true;
    });

    if (animateSwitch) this.startLeaving(previous);
  }

  close() {
    this.clearTimers();

    if (!this.classList.contains('is-open') || this.classList.contains('is-closing')) {
      return;
    }

    this.activeId = null;
    this.triggers.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));

    if (!this.animationsEnabled()) {
      this.flushLeaving();
      this.finishClose();
      return;
    }

    const activePanel = this.panels.find((panel) => panel.classList.contains('is-active'));
    this.classList.add('is-closing');

    const finish = () => {
      this.clearCloseAnimation();
      this.finishClose();
    };

    if (activePanel) {
      this._closeAnimPanel = activePanel;
      this._closeAnimHandler = (event) => {
        if (event.target !== activePanel) return;
        finish();
      };
      activePanel.addEventListener('animationend', this._closeAnimHandler);
    }

    this.closeAnimTimer = window.setTimeout(finish, this.getAnimationDuration() + 50);
  }

  finishClose() {
    this.classList.remove('is-open', 'is-closing');
    this.flushLeaving();
    this.panels.forEach((panel) => {
      panel.classList.remove('is-active', 'is-leaving');
      panel.hidden = true;
    });
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      const activeTrigger = this.triggers.find((btn) => btn.getAttribute('aria-expanded') === 'true');
      this.close();
      if (activeTrigger) activeTrigger.focus();
    }
  }
}

customElements.define('mega-menu', MegaMenu);
